import { pickerStreamMeasurement } from "./picker-row-text";
import type { PickerStream } from "./picker-stream";

export interface PickerGroup {
    key: string;
    label: string;
    streams: PickerStream[];
}

export type CappedPickerGroup = PickerGroup & { total: number };

const THIS_ZONE_KEY = "this-zone";
const NO_ZONE_KEY = "no-zone";

const compareText = new Intl.Collator(undefined, { numeric: true }).compare;

function compareStreams(a: PickerStream, b: PickerStream, measurementOf: (stream: PickerStream) => string): number {
    if (a.hostName !== b.hostName) {
        if (a.hostName === null) return 1;
        if (b.hostName === null) return -1;
        return compareText(a.hostName, b.hostName);
    }
    const byMeasurement = compareText(measurementOf(a), measurementOf(b));
    if (byMeasurement !== 0) return byMeasurement;
    return (b.heightM ?? 0) - (a.heightM ?? 0);
}

function groupKey(stream: PickerStream): string {
    if (stream.inCurrentZone) return THIS_ZONE_KEY;
    return stream.zoneName === null ? NO_ZONE_KEY : `zone:${stream.zoneName}`;
}

function compareGroups(a: PickerGroup, b: PickerGroup): number {
    if (a.key === b.key) return 0;
    if (a.key === THIS_ZONE_KEY || b.key === NO_ZONE_KEY) return -1;
    if (b.key === THIS_ZONE_KEY || a.key === NO_ZONE_KEY) return 1;
    return compareText(a.label, b.label);
}

export function groupPickerStreams(
    streams: readonly PickerStream[],
    labels: { thisZone: string; noZone: string },
    rankById: ReadonlyMap<number, number> = new Map(),
): PickerGroup[] {
    const rank = (stream: PickerStream) => rankById.get(stream.id) ?? 0;
    const measurements = new Map(streams.map((stream) => [stream.id, pickerStreamMeasurement(stream)]));
    const measurementOf = (stream: PickerStream) => measurements.get(stream.id) ?? "";

    const groups = new Map<string, PickerGroup>();
    streams.forEach((stream) => {
        const key = groupKey(stream);
        const group = groups.get(key);
        if (group) {
            group.streams.push(stream);
            return;
        }
        let label = stream.zoneName ?? labels.noZone;
        if (key === THIS_ZONE_KEY) label = labels.thisZone;
        groups.set(key, { key, label, streams: [stream] });
    });

    const bestRanks = new Map(
        [...groups.values()].map((group) => [
            group.key,
            group.streams.reduce((best, stream) => Math.min(best, rank(stream)), Infinity),
        ]),
    );
    const bestRank = (group: PickerGroup) => bestRanks.get(group.key) ?? 0;

    const sorted = [...groups.values()].sort((a, b) => bestRank(a) - bestRank(b) || compareGroups(a, b));
    sorted.forEach((group) => group.streams.sort((a, b) => rank(a) - rank(b) || compareStreams(a, b, measurementOf)));
    return sorted;
}

export function capPickerGroups(groups: readonly PickerGroup[], limit: number): CappedPickerGroup[] {
    let remaining = limit;
    return groups.flatMap((group) => {
        if (remaining <= 0) return [];
        const streams = group.streams.slice(0, remaining);
        remaining -= streams.length;
        return [{ ...group, streams, total: group.streams.length }];
    });
}

/** The zone's own streams count as this zone by where they came from; their fragments may not say so. */
export function groupDisplayedStreams(
    plotted: readonly PickerStream[],
    addedIds: ReadonlySet<number>,
    labels: { thisZone: string; noZone: string },
): { zoneStreams: PickerStream[]; addedByZone: PickerGroup[] } {
    return {
        zoneStreams: plotted.filter((stream) => !addedIds.has(stream.id) || stream.inCurrentZone),
        addedByZone: groupPickerStreams(
            plotted.filter((stream) => addedIds.has(stream.id) && !stream.inCurrentZone),
            labels,
        ),
    };
}
